import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { chain, get, cloneDeep, size, min } from "lodash"
import { takeLatest, call, select, put } from "redux-saga/effects"
import { sendQuery } from "api/graphql"
import { Creators as modelCreators } from "model/data"
import { LOADING, OK, ERROR } from "helpers/data-statuses"
import { Types as GraphqlTypes } from "model/graphql"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Types as SaveExperimentTypes } from "modules/save-experiment/model"
import { getOptimizationConstraints, getOptimizationPositionSet, getOptimizationUniverseSort, getSecuritiesCount, getOptimizationOptions } from "./selectors"
import { getSelectedDate } from "./selectors/dates"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { getModelId } from "./selectors/model"
import { getFacetedFilter } from "modules/securities-finder/selectors/filter"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { NORMALIZATION } from "helpers/segments"

export const SECURITIES_TAKE = 5000

export const { Types, Creators } = actionCreator("ompnt/modules/smartTrade", {
  submitStepSuccess: ["step"],
  resetSmartTrade: [],
  setupTrade: ["setupTrade"],
  setScorecardDate: ["date"],
  setSelectedComparisonType: {
    props: ["selectedComparisonType"],
    track: true,
    trackType: "smart-trade-compare-tab-switch",
    trackPayload: ({ selectedComparisonType }) => ({ tab: selectedComparisonType }),
  },
  trackPanelTrigger: {
    props: ["panel"],
    track: true,
    trackType: "smart-trade-accordion-triggered",
    trackPayload: ({ panel }) => ({ accordion: panel }),
  },
  trackSmartTradeStepper: {
    props: ["action", "step"],
    track: true,
    trackType: "smart-trade-stepper",
  },
  setSelectedDate: ["selectedDate"],
  resetSelectedDate: [],
  setPeriod: {
    props: ["period"],
    track: true,
    trackType: "smart-trade-date-period-updated",
    trackPayload: ({ period }) => ({ period }),
  },
  setRules: ["optimizationRules"],
  setModel: ["modelId"],
  resetRules: [],
  optimizationScanner: [],
  optimizationSuccess: ["optimizationResult"],
  optimizationLoading: [],
  optimizationError: [],
  adjustSecurityCount: ["count"],
  notifyUniverseSecurities: [],
  applyRemovedSecurities: [],
  loadSmartTrade: ["config", "modelId"],
  setSegmentNormalization: ["itemType", "segmentNormalizationType"],
})

const DEFAULT_BASKET_RULES = {
  basketSize: -100,
  maxTradeADV: "",
  maxConcentration: "",
  targetPositions: "",
}

const DEFAULT_STATE = {
  activeStep: 0,
  completedStep: null,
  modelId: null,
  setup: {
    tradeDate: null,
    tradeSize: null,
    basketRules: DEFAULT_BASKET_RULES,
    smartTradeRules: null,
  },
  selectedComparisonType: "scorecard",
  scorecardDate: null,
  selectedDate: null,
  period: "1Y",
  optimizationStatus: LOADING,
  optimizationData: null,
  adjustedSecuritiesCount: null,
  isModified: false,
  segmentNormalization: {
    refInstrument: NORMALIZATION.STANDALONE,
    basket: NORMALIZATION.STANDALONE,
  },
}

function handleAdjustSecurityCount(state, { count }) {
  return { ...state, adjustedSecuritiesCount: count }
}

function handleSubmitStepSuccess(state, { step }) {
  return { ...state, activeStep: step, completedStep: state.completedStep < step ? step : state.completedStep, adjustedSecuritiesCount: null }
}

function handleNavigate(state, action) {
  if (!chain(action).get("location.pathname", null).includes("smart-trade").value()) {
    return { ...state, ...DEFAULT_STATE }
  }

  return state
}

function handleLoadSmartTrade(state, { config, modelId }) {
  return { ...state,
    ...DEFAULT_STATE,
    activeStep: 2,
    completedStep: 2,
    modelId,
    optimizationStatus: OK, // Because we don't run an optimization when we first edit a smart trade
    setup: cloneDeep(config.setup),
  }
}

function handleSetupTrade(state, { setupTrade }) {
  if (setupTrade.tradeSize && !setupTrade.portfolio) {
    setupTrade.tradeSize *= 1000000
  }

  return {
    ...state,
    selectedDate: setupTrade.tradeDate,
    scorecardDate: setupTrade.tradeDate,
    setup: { ...state.setup, ...setupTrade },
    isModified: true,
  }
}

function handleSetSelectedComparisonType(state, { selectedComparisonType }) {
  return { ...state, selectedComparisonType }
}

function handleSetScorecardDate(state, { date }) {
  return { ...state, scorecardDate: date.adjusted || date.selectedDate }
}

function handleSetPeriod(state, { period }) {
  return { ...state, period }
}

function handleResetSelectedDate(state) {
  return { ...state, selectedDate: null }
}

function handleSetSelectedDate(state, { selectedDate }) {
  return { ...state, selectedDate }
}

function handleSetModel(state, { modelId }) {
  return { ...state, modelId, setup: { ...state.setup, smartTradeRules: null }, completedStep: null, isModified: true }
}

function handleSetRules(state, { optimizationRules }) {
  return { ...state, setup: { ...state.setup, basketRules: optimizationRules.basketRules, smartTradeRules: optimizationRules.smartTradeRules }, isModified: true }
}

function handleOptimizationLoading(state) {
  return { ...state, optimizationStatus: LOADING }
}

function handleOptimizationSuccess(state, { optimizationResult }) {
  return {
    ...state,
    optimizationData: optimizationResult,
    optimizationStatus: OK,
    isModified: true,
  }
}

function handleOptimizationError(state) {
  return {
    ...state,
    optimizationStatus: ERROR,
    optimizationData: null,
  }
}

function handleResetRules(state) {
  return { ...state, setup: { ...state.setup, basketRules: DEFAULT_BASKET_RULES, smartTradeRules: null }, isModified: true }
}

function handleApplyRemovedSecurities(state) {
  return { ...state, isModified: true }
}

function handleSetSegmentNormalization(state, { itemType, segmentNormalizationType }) {
  let segmentNormalization = {}
  if (itemType === "basket") {
    segmentNormalization = { basket: segmentNormalizationType }
  } else {
    segmentNormalization = { refInstrument: segmentNormalizationType }
  }

  return { ...state, segmentNormalization: { ...state.segmentNormalization, ...segmentNormalization } }
}

const handleLoadSuccess = (state, action) => {
  const { resource, data } = action || {}
  let newState = state
  // For portfolio instruments, the tradeSize must be recalculated whenever the
  // positions query(date change) is triggered
  if (resource === "instrumentPortfolioPositions") {
    const { gmv, equity } = get(data, "portfolio.dates.0", {})
    newState = { ...state,
      setup: { ...state.setup,
        tradeSize: equity || gmv,
        basketRules: {
          ...state.setup.basketRules,
          // Default basket size is not -100 for portfolios as we have a field to choose it
          basketSize: null,
        } } }
  }
  return newState
}

export default createReducer(DEFAULT_STATE, {
  [Types.ADJUST_SECURITY_COUNT]: handleAdjustSecurityCount,
  [Types.SUBMIT_STEP_SUCCESS]: handleSubmitStepSuccess,
  [HistoryTypes.NAVIGATE]: handleNavigate,
  [Types.SETUP_TRADE]: handleSetupTrade,
  [Types.SET_SELECTED_COMPARISON_TYPE]: handleSetSelectedComparisonType,
  [Types.SET_SCORECARD_DATE]: handleSetScorecardDate,
  [Types.SET_SELECTED_DATE]: handleSetSelectedDate,
  [Types.RESET_SELECTED_DATE]: handleResetSelectedDate,
  [Types.SET_PERIOD]: handleSetPeriod,
  [Types.SET_RULES]: handleSetRules,
  [Types.OPTIMIZATION_SUCCESS]: handleOptimizationSuccess,
  [Types.OPTIMIZATION_LOADING]: handleOptimizationLoading,
  [Types.OPTIMIZATION_ERROR]: handleOptimizationError,
  [Types.RESET_RULES]: handleResetRules,
  [Types.SET_MODEL]: handleSetModel,
  [Types.LOAD_SMART_TRADE]: handleLoadSmartTrade,
  [Types.APPLY_REMOVED_SECURITIES]: handleApplyRemovedSecurities,
  [Types.SET_SEGMENT_NORMALIZATION]: handleSetSegmentNormalization,
  [GraphqlTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

function* callOptimization() {
  const { account, modelId, filter, optimizationConstraints, optimizationPositionSet, tradeDate, sort, securitiesSearchCount, options } = yield select((state) => ({
    account: state.global.selected.account,
    modelId: getModelId(state),
    optimizationConstraints: getOptimizationConstraints(state),
    filter: getFacetedFilter(state, { selectedDate: getSelectedDate(state) }),
    sort: getOptimizationUniverseSort(state),
    optimizationPositionSet: getOptimizationPositionSet(state),
    tradeDate: getResearchTopicDate(state),
    securitiesSearchCount: getSecuritiesCount(state),
    options: getOptimizationOptions(state),
  }))
  const { constraints, objective } = optimizationConstraints
  const targetPositions = get(objective, "targetPositions", null)
  const shortLong = constraints.longMarketValue ? "long" : "short"
  const isPortfolio = optimizationPositionSet.type === "PORTFOLIO"

  yield put(Creators.optimizationLoading())

  const query = `query ($modelId: String!, $positionSet: PositionSetInput, $on: [Date], $securitySearch: OptimizationSecuritySearchInput, $objective: [OptimizationObjective]!, $constraints: OptimizationConstraints!, $options: OptimizationOptionsInput) {
    model(id: $modelId) {
      optimization(
        on: $on,
        positionSet: $positionSet,
        objective: $objective,
        securities: {${shortLong}: {securitySearch: $securitySearch}},
        options: $options,
        constraints: $constraints ) {
        positionsDelta {
          date
          equities {
            id {
              ticker
              mic
              bloombergTicker
              internalId
            }
            economicExposure
          }
          swaps {
            id
            economicExposure
          }
          indexes {
            id
            economicExposure
          }
        }
      }
    }
  }`
  const variables = {
    objective,
    modelId,
    positionSet: optimizationPositionSet,
    ...(isPortfolio && { on: tradeDate }),
    securitySearch: { filter, sort, take: min([securitiesSearchCount, SECURITIES_TAKE]) },
    constraints,
    options,
  }

  const result = yield call(sendQuery, query, variables, account)
  const optimizationError = get(result, "apiMessages.errors.0")
  const optimizationMessage = get(result, "apiMessages.messages.0")

  if (optimizationError) {
    yield put(notificationCreators.addApiNotification("error", null, optimizationError.code, optimizationError.message))
    yield put(Creators.optimizationError())
  } else {
    if (optimizationMessage) {
      yield put(notificationCreators.addApiNotification("warning", null, optimizationMessage.code, optimizationMessage.message))
    }

    const delta = get(result, "data.model.optimization.positionsDelta.0")

    // We only count equities and indexes because we don't want baskets returned by the optimizer (the `tradeSwaps` constraint is not enabled).
    // If we were to include baskets, we would not always be able to create a new basket based on these trades since baskets can't include other baskets.
    const nbPositions = size(delta.equities) + size(delta.indexes)
    if (targetPositions && nbPositions > targetPositions) {
      yield put(notificationCreators.add("warning", null, `Positions target could not be achieved - the most efficient factor hedge basket contains ${nbPositions} positions.`))
    }

    if (!delta.equities && !delta.indexes) {
      yield put(notificationCreators.add("error", null, "No position returned by the optimizer."))
      yield put(Creators.optimizationError())
    } else {
      yield put(Creators.optimizationSuccess(delta))
    }
  }
}

function* notifyUniverseSecurities() {
  yield put(notificationCreators.add("warning", null, `Universe was limited to the first ${SECURITIES_TAKE} names in the security search for this smart trade. Consider limiting your universe to less than ${SECURITIES_TAKE}.`))
}

function* handleUpdateSmartTrade() {
  yield put(modelCreators.resetCollections(["smartTradeExperimentPositions"]))
}

export function* saga() {
  yield takeLatest([
    Types.OPTIMIZATION_SCANNER,
    Types.SET_RULES,
    Types.APPLY_REMOVED_SECURITIES,
    Types.RESET_RULES], callOptimization)
  yield takeLatest(Types.NOTIFY_UNIVERSE_SECURITIES, notifyUniverseSecurities)
  yield takeLatest(SaveExperimentTypes.UPDATE_META_AND_UPLOAD_SUCCESS, handleUpdateSmartTrade)
}
